import Immutable from 'immutable';
import { Sheet, SheetList } from 'records/Sheet';
import { RawSheetResponse, SheetResponse } from 'records/SheetResponse';
import { UpdateSheet } from 'records/UpdateSheet';
import GQLRequest from 'utils/GQLRequest';
import { SheetVersion } from '../records/SheetVersion';

export const getNextSheets = async (cursor: string): Promise<SheetResponse> => {
  const { nextSheets } = await new GQLRequest()
    .query(
      `
      query NextSheets($cursor: String!) {
        nextSheets(cursor: $cursor) {
          sheets {
            sheetid
            versions {
              sheetid
              sheetnumber
              sheettitle
              discipline
              sheetDiscipline {
                id
                designator
                description
                sortOrder
              }
              issueddate
              revisioncomment
              publishedtimestamp
              publishedBy
              pageid
              documentid
              projectid
              filename
              documentTitle
              pageLabel
              pageIndex
              width
              height
              uploadedBy
              uploadtimestamp
              processingRequestid
              maptilecloudfronturl
              minzoom
              maxzoom
              tilesize
              drawingtilescloudfronturl
              drawingminzoom
              drawingmaxzoom
              drawingtilesize
            }
          }
          cursor
        }
      }
      `
    )
    .variables({ cursor })
    .json<{ nextSheets: RawSheetResponse }>();

  return SheetResponse.deserialize(nextSheets);
};

export const getSheets = async (projectid: string): Promise<SheetList> => {
  const { sheets } = await new GQLRequest()
    .query(
      `
      query Sheets($projectid: String!, $pageSize: Int!) {
        sheets(projectid: $projectid, pageSize: $pageSize) {
          sheets {
            sheetid
            sheetNumber
            versions {
              sheetid
              sheetnumber
              sheettitle
              discipline
              sheetDiscipline {
                id
                designator
                description
                sortOrder
              }
              issueddate
              revisioncomment
              publishedtimestamp
              publishedBy
              pageid
              documentid
              projectid
              filename
              documentTitle
              pageLabel
              pageIndex
              width
              height
              uploadedBy
              uploadtimestamp
              processingRequestid
              maptilecloudfronturl
              minzoom
              maxzoom
              tilesize
              drawingtilescloudfronturl
              drawingminzoom
              drawingmaxzoom
              drawingtilesize
            }
          }
          cursor
        }
      }
      `
    )
    .variables({ projectid, pageSize: 50 })
    .json<{ sheets: RawSheetResponse }>();

  // For now, just fetching all sheets.
  let sheetsList = Immutable.List(sheets.sheets.map(Sheet.deserialize));
  let { cursor } = sheets;

  while (cursor) {
    const { sheets: nextSheets, cursor: nextCursor } = await getNextSheets(
      cursor
    );

    sheetsList = sheetsList.concat(nextSheets);
    cursor = nextCursor;
  }

  return sheetsList;
};

export const getSheetPDF = async (
  projectid: string,
  sheetid: string,
  documentid: string
): Promise<string> => {
  const result = await new GQLRequest()
    .query(
      `
      query SheetPDFs($projectid: String!, $sheetid: String!, $documentid: String!) {
        pdfExport(projectid: $projectid, sheetid: $sheetid, documentid: $documentid) {
          url
        }
      }
      `
    )
    .variables({ projectid, sheetid, documentid })
    .json<{ pdfExport: { url: string } }>();

  return result.pdfExport.url;
};

export const updateSheetVersionInfo = async (
  sheetData: UpdateSheet
): Promise<SheetVersion[]> => {
  const {
    projectid,
    pageids,
    discipline,
    issueddate,
    revisioncomment,
    sheetDiscipline,
  } = sheetData;

  const { updateSheetVersions } = await new GQLRequest()
    .query(
      `
      mutation UpdateSheetVersionInfo($projectId: String!, $pageIds: [String!]!, $update: UpdateSheetVersionInput!) {
        updateSheetVersions(projectid: $projectId, pageids: $pageIds, update: $update) {
          sheetid
          sheetnumber
          sheettitle
          discipline
          sheetDiscipline {
            id
            designator
            description
          }
          issueddate
          revisioncomment
          publishedtimestamp
          publishedBy
          pageid
          documentid
          projectid
          filename
          documentTitle
          pageLabel
          pageIndex
          width
          height
          uploadedBy
          uploadtimestamp
          processingRequestid
        }
      }
    `
    )
    .variables({
      projectId: projectid,
      pageIds: pageids,
      update: {
        discipline,
        sheetDiscipline,
        issueddate,
        revisioncomment,
      },
    })
    .json<{ updateSheetVersions: SheetVersion[] }>();

  return updateSheetVersions;
};

export default {
  getSheets,
  getSheetPDF,
  updateSheetVersionInfo,
};
